import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { nullToEmpty } from "@impero-com/common/utils/string";

import { ToggleButton, EmptyToggleButton } from "components/toggleButton";

import * as theme from "./theme.css";

class TableErrorList extends React.PureComponent<TableErrorListProps> {
    render() {
        const { rowErrorGroups } = this.props;
        if (rowErrorGroups.length === 0) {
            return null;
        }
        return (
            <Tree className={theme.errorList}>
                <TreeNode
                    className={theme.errorHeader}
                    title={t("Table_ErrorList_Title")}
                >
                    {rowErrorGroups.map((err, index) => (
                        <TreeNode
                            key={index.toString()}
                            title={`${t("Table_ErrorList_AtRow")} ${
                                err.rowNumber
                            } `}
                            className={theme.errorRow}
                            initialStatus="closed"
                        >
                            {err.rowErrors.length > 0
                                ? err.rowErrors.map((rowErr, idx) => (
                                      <TreeNode
                                          key={idx.toString()}
                                          title={rowErr}
                                      />
                                  ))
                                : null}
                            {err.cellErrors.length > 0
                                ? err.cellErrors.map((cellErr, idx) => (
                                      <TreeNode
                                          key={idx.toString()}
                                          title={`${t(
                                              "Table_ErrorList_AtColumn"
                                          )} "${cellErr.colLabel}"`}
                                      >
                                          {cellErr.errors.map(
                                              (err, cellIdx) => (
                                                  <TreeNode
                                                      key={cellIdx.toString()}
                                                      title={err}
                                                  />
                                              )
                                          )}
                                      </TreeNode>
                                  ))
                                : null}
                        </TreeNode>
                    ))}
                </TreeNode>
            </Tree>
        );
    }
}

interface RowErrorGroup {
    rowNumber: number;
    rowErrors: Array<string>;
    cellErrors: Array<{ colLabel: string; errors: Array<string> }>;
}

interface TableErrorListProps {
    rowErrorGroups: Array<RowErrorGroup>;
}

class Tree extends React.PureComponent<TreeProps> {
    render() {
        const { className, children } = this.props;
        return <div className={`${theme.tree} ${className}`}>{children}</div>;
    }
}

interface TreeProps {
    className?: string;
    children: React.ReactNode;
}

class TreeNode extends React.Component<TreeNodeProps, TreeNodeState> {
    constructor(props: TreeNodeProps) {
        super(props);
        this.state = {
            status: props.initialStatus ? props.initialStatus : "open",
        };
    }
    render() {
        const { title, className, children } = this.props;
        const { status } = this.state;
        return (
            <div className={theme.treeNode}>
                <div
                    className={`${theme.treeNodeTitle} ${nullToEmpty(
                        className
                    )}`}
                    onClick={this.handleClick}
                >
                    {children ? (
                        <ToggleButton
                            className={theme.toggleButton}
                            isCollapsed={status === "closed"}
                            onToggle={this.handleClick}
                        />
                    ) : (
                        <EmptyToggleButton className={theme.toggleButton} />
                    )}
                    {title}
                </div>
                {status === "open" ? children : null}
            </div>
        );
    }
    private handleClick = () => {
        const { status } = this.state;
        switch (status) {
            case "open":
                this.setState({ status: "closed" });
                break;
            case "closed":
                this.setState({ status: "open" });
                break;
            default:
                assertNever(status);
        }
    };
}

interface TreeNodeProps {
    title: string;
    initialStatus?: NodeStatus;
    className?: string;
    children?: React.ReactNode;
}

interface TreeNodeState {
    status: NodeStatus;
}

type NodeStatus = "open" | "closed";

export { TableErrorList, type TableErrorListProps, type RowErrorGroup };
